import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import {
  ContentCopy,
  Download,
  Image,
  Link as LinkIcon,
  QrCode2,
} from '@mui/icons-material'
import qrcode from 'qrcode'
import SmallModal from '../Modals/SmallModal'
import { GlobalContext } from '../../App'

const wrapText = (context, text, maxWidth) => {
  const words = String(text || '').split(' ')
  const lines = []
  let line = ''

  words.forEach((word) => {
    const nextLine = line ? `${line} ${word}` : word
    if (context.measureText(nextLine).width <= maxWidth) {
      line = nextLine
      return
    }

    if (line) lines.push(line)
    line = word
  })

  if (line) lines.push(line)
  return lines.slice(0, 2)
}

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
        return
      }

      reject(new Error('Unable to create QR image.'))
    }, 'image/png')
  })

const drawRoundedRect = (context, x, y, width, height, radius) => {
  context.beginPath()
  context.moveTo(x + radius, y)
  context.lineTo(x + width - radius, y)
  context.quadraticCurveTo(x + width, y, x + width, y + radius)
  context.lineTo(x + width, y + height - radius)
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  context.lineTo(x + radius, y + height)
  context.quadraticCurveTo(x, y + height, x, y + height - radius)
  context.lineTo(x, y + radius)
  context.quadraticCurveTo(x, y, x + radius, y)
  context.closePath()
}

const QRCodePopup = ({ open, onClose, url, title, imageLabel, scanHint }) => {
  const composedCanvasRef = useRef()
  const [previewSrc, setPreviewSrc] = useState(null)
  const [loading, setLoading] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const label = imageLabel || title || 'QR code'
  const urlString = url?.toString?.() || String(url || '')

  const drawQrImage = async () => {
    if (!open || !urlString) return

    setLoading(true)

    try {
      const qrCanvas = document.createElement('canvas')
      await qrcode.toCanvas(qrCanvas, urlString, {
        width: 260,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: {
          dark: '#242F40',
          light: '#FFFFFF',
        },
      })

      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      const padding = 28
      const qrSize = 260
      const width = qrSize + padding * 2

      context.font =
        '700 22px Poppins, Roboto, Arial, Helvetica, sans-serif'
      const titleLines = wrapText(context, label, width - padding * 2)
      const titleHeight = Math.max(titleLines.length, 1) * 28
      const footerHeight = 36

      canvas.width = width
      canvas.height = padding + qrSize + 22 + titleHeight + footerHeight

      context.fillStyle = '#FFFFFF'
      context.fillRect(0, 0, canvas.width, canvas.height)

      context.fillStyle = '#F8FAFC'
      context.fillRect(0, 0, canvas.width, canvas.height)

      context.fillStyle = '#FFFFFF'
      context.strokeStyle = '#E2E8F0'
      context.lineWidth = 2
      drawRoundedRect(context, 14, 14, canvas.width - 28, canvas.height - 28, 18)
      context.fill()
      context.stroke()

      context.drawImage(qrCanvas, padding, padding, qrSize, qrSize)

      context.textAlign = 'center'
      context.textBaseline = 'top'
      context.fillStyle = '#242F40'
      context.font =
        '800 22px Poppins, Roboto, Arial, Helvetica, sans-serif'

      titleLines.forEach((line, index) => {
        context.fillText(line, canvas.width / 2, padding + qrSize + 20 + index * 28)
      })

      context.fillStyle = '#64748B'
      context.font =
        '600 12px Poppins, Roboto, Arial, Helvetica, sans-serif'
      context.fillText(
        scanHint || 'Scan to open medicine bag check',
        canvas.width / 2,
        canvas.height - 42,
      )

      composedCanvasRef.current = canvas
      setPreviewSrc(canvas.toDataURL('image/png'))
    } catch {
      setToast({
        message: 'Unable to load QR code. Please try again later.',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    drawQrImage()
  }, [open, urlString, label])

  const copyImage = async () => {
    try {
      if (!composedCanvasRef.current) {
        await drawQrImage()
      }

      const blob = await canvasToBlob(composedCanvasRef.current)

      if (!window.ClipboardItem || !navigator.clipboard?.write) {
        throw new Error('Image clipboard is not supported.')
      }

      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ])

      setToast({
        message: 'QR image copied to clipboard.',
        severity: 'success',
      })
    } catch {
      navigator.clipboard
        ?.writeText(urlString)
        .then(() => {
          setToast({
            message:
              'Image copy is not supported in this browser, so the URL was copied instead.',
            severity: 'warning',
          })
        })
        .catch(() => {
          setToast({
            message: 'Failed to copy QR code.',
            severity: 'error',
          })
        })
    }
  }

  const downloadImage = async () => {
    try {
      if (!composedCanvasRef.current) {
        await drawQrImage()
      }

      const blob = await canvasToBlob(composedCanvasRef.current)
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = `${
        label.replace(/[^\w\- ]+/g, '').trim() || 'QR Code'
      } QR.png`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(objectUrl)
    } catch {
      setToast({
        message: 'Failed to download QR code.',
        severity: 'error',
      })
    }
  }

  const copyUrl = () => {
    navigator.clipboard
      .writeText(urlString)
      .then(() => {
        setToast({
          message: 'URL copied to clipboard.',
          severity: 'success',
        })
      })
      .catch(() => {
        setToast({
          message: 'Failed to copy URL to clipboard.',
          severity: 'error',
        })
      })
  }

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      style={{
        width: 'min(92vw, 430px)',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: 0,
        borderRadius: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid rgba(15,23,42,0.08)',
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: '#242F40',
            color: 'white',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
              }}
            >
              <QrCode2 />
            </Box>
            <Stack minWidth={0}>
              <Typography fontWeight={950} noWrap color='white'>
                {title || 'QR Code'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }} noWrap>
                Copy or download as a labelled image
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={1.5} sx={{ p: 2 }} alignItems="center">
          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              width: '100%',
              borderRadius: 2,
              border: '1px solid rgba(15,23,42,0.08)',
              backgroundColor: '#F8FAFC',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 330,
                minHeight: 330,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {loading ? (
                <Typography variant="body2" color="text.secondary">
                  Generating QR code...
                </Typography>
              ) : (
                previewSrc && (
                  <Box
                    component="img"
                    src={previewSrc}
                    alt={label}
                    sx={{ width: '100%', maxWidth: 330, height: 'auto' }}
                  />
                )
              )}
            </Box>
          </Paper>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              icon={<Image />}
              label="PNG image"
              sx={{ borderRadius: 1, fontWeight: 850 }}
            />
            <Chip
              size="small"
              icon={<ContentCopy />}
              label="Name included"
              sx={{ borderRadius: 1, fontWeight: 850 }}
            />
          </Stack>

          <Divider flexItem />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            width="100%"
          >
            <Button
              startIcon={<Image />}
              onClick={copyImage}
              size="small"
              variant="contained"
              fullWidth
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 900,
                backgroundColor: '#1E7F88',
              }}
            >
              Copy QR Image
            </Button>
            <Button
              startIcon={<Download />}
              onClick={downloadImage}
              size="small"
              variant="outlined"
              fullWidth
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
            >
              Download
            </Button>
            <Button
              startIcon={<LinkIcon />}
              onClick={copyUrl}
              size="small"
              variant="outlined"
              fullWidth
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
            >
              Copy URL
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </SmallModal>
  )
}

export default QRCodePopup
